import { useState, useEffect } from 'react';
import { Flex, View, Link, ToggleButton } from '@adobe/react-spectrum';
import { Grid, Fullscreen } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import styles from './header_component.module.css';
import FilterSearchTextBox from './FilterSearchTextBox';

interface IHeaderComponentProps {
  onPageViewChange: (view: string) => void;
  autoChanged: string;
  filterParams: any;
  onSearchChange: (params: {}) => void;
  fpaTypeCategory: string;
  onAddNew: () => void;
}

const HeaderComponent: React.FC<IHeaderComponentProps> = ({ 
  onPageViewChange, autoChanged, filterParams, onSearchChange, fpaTypeCategory, onAddNew }) => {
  const { t } = useTranslation('issue');
  const _t = (key: string) => t(`issue_filters.${key}`, { ns: 'issue' });
  const [selectedButton, setSelectedButton] = useState<string>('Grid');
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    setSelectedButton(prev => (prev === autoChanged ? prev : autoChanged));
  }, [autoChanged]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggle = (view: string) => {
    setSelectedButton(prev => (prev === view ? prev : view));
    onPageViewChange(view);
  };

  const isCompact = windowWidth < 768;
  const isSmallTablet = windowWidth >= 768 && windowWidth < 1024;
  const isMacBookAir = windowWidth >= 1024 && windowWidth <= 1440; // Mac Air range
  return (
    <Flex 
      direction={isCompact ? 'column' : 'row'} 
      justifyContent={'space-between'} 
      gap={isCompact ? 'size-150' : 'size-0'}
      UNSAFE_className={styles.header_parent}
      wrap={isSmallTablet}
    >
      {/* Left Section - Add New and Search */}
      <Flex 
        direction={'row'} 
        gap={isCompact ? 'size-100' : (isSmallTablet || isMacBookAir ? 'size-150' : 'size-200')}
        alignItems={'center'}
        justifyContent={isCompact ? 'space-between' : 'start'}
        width={isCompact ? '100%' : 'auto'}
        wrap={isSmallTablet || isMacBookAir}
      >
        <Link
          isQuiet
          onPress={e => {
            onAddNew();
          }}
          UNSAFE_className={`${styles.add_new_btn} ${isCompact ? styles.add_new_btn_compact : ''}`}
        >
          <i className="bi bi-plus fs-5">
            <View UNSAFE_className={styles.add_new_btn_text}>
              {isCompact ? t('add', { ns: 'layout_components' }) : t('add_new', { ns: 'layout_components' })}
            </View>
          </i>
        </Link>

        <Flex direction={'column'} width={isCompact ? '100%' : '217px'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.activity_search_input}>
          <Flex direction={'row'} height={'32px'} alignSelf={'stretch'} flexShrink={0} alignItems={'center'} justifyContent={'end'} UNSAFE_className={styles.activity_search}>
            <FilterSearchTextBox
              onSearchChange={onSearchChange}
              filterParams={filterParams}
              filterKey="contains"
              ariaLabel={_t('search_by_name')}
              placeholder={_t('search_by_name')}
            />
            <i className={`${styles.icon_activity_search} bi bi-search`}></i>
          </Flex>
        </Flex>

     <View UNSAFE_className={styles.frame_divider}></View>
      </Flex>
      <Flex 
        direction={'row'} 
        gap={isCompact ? 'size-100' : (isSmallTablet || isMacBookAir ? 'size-75' : 'size-100')}
        alignItems={'center'}
        justifyContent={isCompact ? 'space-between' : 'end'}
        width={isCompact ? '100%' : 'auto'}
      >
        <Flex direction={'row'} gap={'size-50'} alignItems={'center'}>
          <ToggleButton 
            isSelected={selectedButton === 'Grid'} 
            onChange={() => handleToggle('Grid')} 
            isEmphasized
            UNSAFE_className={styles.toggle_button}
          >
            <Grid size={isCompact ? 16 : (isMacBookAir ? 18 : 20)} />
          </ToggleButton>

          <ToggleButton 
            isSelected={selectedButton === 'Full'} 
            onChange={() => handleToggle('Full')} 
            isEmphasized
            UNSAFE_className={styles.toggle_button}
          >
            <Fullscreen size={isCompact ? 16 : (isMacBookAir ? 18 : 20)} />
          </ToggleButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default HeaderComponent;
